/**
 * 샵관리(SH) 로컬 스토어 + 업무규칙
 * 설계: docs/agent-plans/CM/20260919-샵관리-모듈설계.md
 *
 * mapper/BE 연결 전 localStorage. 키: erp.shop.{table}|{tenant}
 * FIFO 출고·시술 트랜잭션 등 업무규칙은 BE(ShStockService/ShRecordService)로 옮길 예정이므로
 * 화면은 이 파일의 함수만 호출하고 localStorage 를 직접 만지지 않는다.
 * 수량은 "사용단위" 기준(입고 시 매입단위 × 환산수).
 */

const PREFIX = 'erp.shop';

export const TABLES = [
  'product', 'stockIn', 'stockOut', 'customer', 'service', 'serviceItem',
  'pass', 'record', 'recordItem', 'sale', 'expense', 'expenseTpl', 'reservation', 'receiptDraft', 'marginItem',
];

export const YN_OPTIONS = [{ value: 'Y', label: 'Y' }, { value: 'N', label: 'N' }];
export const OUT_REASON_OPTIONS = [
  { value: 'SERVICE', label: '시술사용' },
  { value: 'SALE', label: '제품판매' },
  { value: 'DISCARD', label: '폐기' },
  { value: 'ADJUST', label: '재고조정' },
];
export const MANUAL_OUT_REASON_OPTIONS = OUT_REASON_OPTIONS.filter((o) => o.value !== 'SERVICE');
export const PAY_METHOD_OPTIONS = [
  { value: 'CARD', label: '카드' },
  { value: 'CASH', label: '현금' },
  { value: 'TRANSFER', label: '계좌이체' },
  { value: 'ETC', label: '기타' },
];
export const SALE_TYPE_OPTIONS = [
  { value: 'SERVICE', label: '서비스' },
  { value: 'PASS', label: '회원권' },
  { value: 'PRODUCT', label: '제품' },
];
export const PASS_STATUS_OPTIONS = [
  { value: 'ACTIVE', label: '이용중' },
  { value: 'DONE', label: '소진' },
  { value: 'EXPIRED', label: '만료' },
  { value: 'REFUND', label: '환불' },
];
export const EXP_CATEGORY_OPTIONS = [
  { value: 'RENT', label: '임대료' },
  { value: 'COMM', label: '통신비' },
  { value: 'UTIL', label: '수도광열비' },
  { value: 'LABOR', label: '인건비' },
  { value: 'ETC', label: '기타' },
];
export const EXP_UTIL_OPTIONS = [
  { value: '', label: '-' },
  { value: 'WATER', label: '수도' },
  { value: 'ELEC', label: '전기' },
  { value: 'GAS', label: '가스' },
];

const label = (opts, v) => opts.find((o) => o.value === v)?.label || v || '';
export const expCategoryLabel = (v) => label(EXP_CATEGORY_OPTIONS, v);
export const expUtilLabel = (v) => label(EXP_UTIL_OPTIONS, v);

/* ───────── 공통 유틸 ───────── */

export function createUid(prefix = 'SH') {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export const digitsOnly = (v) => String(v || '').replace(/\D/g, '');

export function formatPhone(v) {
  const d = digitsOnly(v);
  if (d.length === 11) return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  return String(v || '').trim();
}

/** 로컬 시간 기준 'yyyy-MM-dd HH:mm' (toISOString 은 UTC 라 한국 새벽엔 어제 날짜가 나옴) */
export const nowLocal = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};
export const today = () => nowLocal().slice(0, 10);
export const thisYm = () => today().slice(0, 7).replace('-', '');

export const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
export const fmt = (v) => num(v).toLocaleString('ko-KR');
const round = (v) => Math.round(num(v));

export function nextSortOrderBy10(rows) {
  const max = (rows || []).reduce((m, r) => Math.max(m, num(r?.sortOrder)), 0);
  return Math.ceil((max + 1) / 10) * 10;
}

export function nextCode(rows, field, prefix, width = 3) {
  let max = 0;
  const re = new RegExp(`^${prefix}(\\d+)$`, 'i');
  for (const r of rows || []) {
    const m = String(r?.[field] || '').match(re);
    if (m) max = Math.max(max, Number(m[1]));
  }
  return `${prefix}${String(max + 1).padStart(width, '0')}`;
}

/* ───────── 테이블 입출력 ───────── */

const key = (tc, name) => `${PREFIX}.${name}|${tc || ''}`;

export function loadTable(tc, name) {
  try {
    const raw = localStorage.getItem(key(tc, name));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveTable(tc, name, rows) {
  localStorage.setItem(key(tc, name), JSON.stringify(rows));
}

const strip = (row) => {
  const { mod, _tempId, ...rest } = row || {};
  return rest;
};
const live = (rows) => (rows || []).filter((r) => r && r.mod !== 'D');

/**
 * 그리드 저장 공통 — 현재 필터 밖 행은 유지, 필터 안 행만 그리드 내용으로 치환.
 * @param {(row)=>object} normalize 저장 직전 행 정리
 */
export function persistTable(tc, name, uidKey, gridRows, filterFn, normalize = (r) => r) {
  const prev = loadTable(tc, name);
  const inFilter = new Set(prev.filter(filterFn).map((r) => r[uidKey]));
  const outside = prev.filter((r) => !inFilter.has(r[uidKey]));
  const active = live(gridRows).map((r) => normalize({ ...strip(r), [uidKey]: r[uidKey] || createUid() }));
  saveTable(tc, name, [...outside, ...active]);
  return active;
}

const byId = (rows, k) => new Map((rows || []).map((r) => [r[k], r]));

/* ───────── 옵션(콤보) ───────── */

export function productOptions(tc) {
  return loadTable(tc, 'product')
    .filter((p) => p.useYn !== 'N')
    .map((p) => ({ value: p.productUid, label: `${p.productCode} ${p.productNm}` }));
}
export function customerOptions(tc) {
  return loadTable(tc, 'customer')
    .filter((c) => c.useYn !== 'N')
    .map((c) => ({ value: c.customerUid, label: `${c.customerNm} (${c.customerNo})` }));
}
export function serviceOptions(tc) {
  return loadTable(tc, 'service')
    .filter((s) => s.useYn !== 'N')
    .map((s) => ({ value: s.serviceUid, label: `${s.serviceCode} ${s.serviceNm}` }));
}

/* ───────── 재고 / FIFO ───────── */

const fifoOrder = (a, b) =>
  String(a.inDate).localeCompare(String(b.inDate)) || String(a.createdAt || '').localeCompare(String(b.createdAt || ''));

/**
 * FIFO 출고 계획 — lots(메모리) 를 직접 차감하고 lot 별 출고 행을 돌려준다. 저장은 호출자가 한다(원자성).
 * 재고가 모자라면 예외(설계 결정 ①: 차단).
 */
export function planFifo(lots, product, qty, { outDate, reason, recordUid = '', remark = '' }) {
  const need = num(qty);
  const cands = lots.filter((l) => l.productUid === product.productUid && num(l.remainQty) > 0).sort(fifoOrder);
  const have = cands.reduce((s, l) => s + num(l.remainQty), 0);
  if (have + 1e-9 < need) {
    throw new Error(`재고 부족: ${product.productNm} (재고 ${fmt(have)}${product.useUnit}, 필요 ${fmt(need)}${product.useUnit})`);
  }
  let left = need;
  const outs = [];
  for (const lot of cands) {
    if (left <= 1e-9) break;
    const take = Math.min(left, num(lot.remainQty));
    lot.remainQty = Math.round((num(lot.remainQty) - take) * 10000) / 10000;
    left = Math.round((left - take) * 10000) / 10000;
    outs.push({
      outUid: createUid('OUT'),
      productUid: product.productUid,
      lotUid: lot.lotUid,
      outDate,
      outQty: take,
      unitCost: num(lot.unitCost),
      amount: round(take * num(lot.unitCost)),
      reason,
      recordUid,
      remark,
      createdAt: new Date().toISOString(),
    });
  }
  return outs;
}

/** 수동 출고(제품판매·폐기·조정) 저장 */
export function issueManual(tc, { productUid, outDate, qty, reason, remark }) {
  const product = loadTable(tc, 'product').find((p) => p.productUid === productUid);
  if (!product) throw new Error('제품을 선택하세요.');
  const lots = loadTable(tc, 'stockIn');
  const outs = planFifo(lots, product, qty, { outDate, reason, remark });
  saveTable(tc, 'stockIn', lots);
  saveTable(tc, 'stockOut', [...loadTable(tc, 'stockOut'), ...outs]);
  return outs;
}

/** 출고 행 삭제 = lot 잔량 복원 (시술 연결 출고는 시술기록 삭제로만) */
export function revertOuts(tc, outUids) {
  const targets = new Set(outUids);
  const outs = loadTable(tc, 'stockOut');
  const lots = loadTable(tc, 'stockIn');
  const lotMap = byId(lots, 'lotUid');
  for (const o of outs.filter((x) => targets.has(x.outUid))) {
    const lot = lotMap.get(o.lotUid);
    if (lot) lot.remainQty = Math.round((num(lot.remainQty) + num(o.outQty)) * 10000) / 10000;
  }
  saveTable(tc, 'stockIn', lots);
  saveTable(tc, 'stockOut', outs.filter((x) => !targets.has(x.outUid)));
}

/** 제품별 재고 요약 */
export function stockSummary(tc) {
  const products = loadTable(tc, 'product');
  const lots = loadTable(tc, 'stockIn');
  return products.map((p) => {
    const pl = lots.filter((l) => l.productUid === p.productUid && num(l.remainQty) > 0).sort(fifoOrder);
    const qty = pl.reduce((s, l) => s + num(l.remainQty), 0);
    const amount = pl.reduce((s, l) => s + num(l.remainQty) * num(l.unitCost), 0);
    return {
      productUid: p.productUid,
      productCode: p.productCode,
      productNm: p.productNm,
      category: p.category,
      useUnit: p.useUnit,
      useYn: p.useYn,
      remainQty: qty,
      avgCost: qty ? amount / qty : 0,
      headCost: pl.length ? num(pl[0].unitCost) : num(p.baseCost),
      stockAmount: round(amount),
      safetyQty: num(p.safetyQty),
      lotCount: pl.length,
      lowStock: num(p.safetyQty) > 0 && qty < num(p.safetyQty),
      nearExpiry: pl.find((l) => l.expiryDate)?.expiryDate || '',
    };
  });
}

export function lotsOf(tc, productUid) {
  return loadTable(tc, 'stockIn')
    .filter((l) => l.productUid === productUid)
    .sort(fifoOrder);
}

/* ───────── 서비스 / 원가 ───────── */

export function serviceItemsOf(tc, serviceUid) {
  return loadTable(tc, 'serviceItem').filter((i) => i.serviceUid === serviceUid);
}

/** 1회 표준원가(미리보기) = Σ 사용량 × 현재 FIFO 선두 lot 단가 */
export function serviceStdCost(tc, serviceUid) {
  const stock = byId(stockSummary(tc), 'productUid');
  return round(serviceItemsOf(tc, serviceUid).reduce((s, i) => s + num(i.useQty) * (stock.get(i.productUid)?.headCost || 0), 0));
}

/* ───────── 회원권 ───────── */

export const passRemain = (p) => Math.max(0, num(p.totalCnt) - num(p.usedCnt));

export function activePassesOf(tc, customerUid, serviceUid) {
  return loadTable(tc, 'pass').filter(
    (p) => p.customerUid === customerUid && p.status === 'ACTIVE'
      && (!serviceUid || p.serviceUid === serviceUid) && passRemain(p) > 0
  );
}

/* ───────── 시술기록 (한 트랜잭션) ───────── */

/**
 * 시술기록 저장: 기록 + 사용제품 FIFO 출고(원가 확정) + 회원권 회차 차감.
 * 실패 시 아무것도 저장하지 않는다.
 */
export function saveRecord(tc, { customerUid, serviceUid, passUid, visitDt, staffNm, remark, items }) {
  if (!customerUid) throw new Error('고객을 선택하세요.');
  if (!serviceUid) throw new Error('서비스를 선택하세요.');
  const products = byId(loadTable(tc, 'product'), 'productUid');
  const lots = loadTable(tc, 'stockIn');
  const passes = loadTable(tc, 'pass');
  const pass = passUid ? passes.find((p) => p.passUid === passUid) : null;
  if (passUid) {
    if (!pass || pass.status !== 'ACTIVE') throw new Error('이용 가능한 회원권이 아닙니다.');
    if (passRemain(pass) <= 0) throw new Error('회원권 잔여횟수가 없습니다.');
  }
  const recordUid = createUid('REC');
  const outs = [];
  const usable = (items || []).filter((i) => i.productUid && num(i.useQty) > 0);
  for (const it of usable) {
    const product = products.get(it.productUid);
    if (!product) throw new Error('삭제된 제품이 포함돼 있습니다.');
    outs.push(...planFifo(lots, product, it.useQty, {
      outDate: String(visitDt).slice(0, 10), reason: 'SERVICE', recordUid, remark: '시술사용',
    }));
  }
  const costAmount = outs.reduce((s, o) => s + o.amount, 0);
  let sessionNo = null;
  if (pass) {
    pass.usedCnt = num(pass.usedCnt) + 1;
    sessionNo = pass.usedCnt;
    if (passRemain(pass) === 0) pass.status = 'DONE';
  }
  const record = {
    recordUid, visitDt, customerUid, serviceUid, passUid: passUid || '', sessionNo,
    staffNm: staffNm || '', costAmount, remark: remark || '', createdAt: new Date().toISOString(),
  };
  saveTable(tc, 'stockIn', lots);
  saveTable(tc, 'stockOut', [...loadTable(tc, 'stockOut'), ...outs]);
  saveTable(tc, 'pass', passes);
  saveTable(tc, 'recordItem', [
    ...loadTable(tc, 'recordItem'),
    ...usable.map((i) => ({ itemUid: createUid('RI'), recordUid, productUid: i.productUid, useQty: num(i.useQty) })),
  ]);
  saveTable(tc, 'record', [...loadTable(tc, 'record'), record]);
  return record;
}

/** 시술기록 삭제 — 출고·회차 역처리(lot 잔량 복원) */
export function deleteRecord(tc, recordUid) {
  const outs = loadTable(tc, 'stockOut').filter((o) => o.recordUid === recordUid).map((o) => o.outUid);
  if (outs.length) revertOuts(tc, outs);
  const rec = loadTable(tc, 'record').find((r) => r.recordUid === recordUid);
  if (rec?.passUid) {
    const passes = loadTable(tc, 'pass');
    const p = passes.find((x) => x.passUid === rec.passUid);
    if (p) {
      p.usedCnt = Math.max(0, num(p.usedCnt) - 1);
      if (p.status === 'DONE') p.status = 'ACTIVE';
      saveTable(tc, 'pass', passes);
    }
  }
  saveTable(tc, 'recordItem', loadTable(tc, 'recordItem').filter((i) => i.recordUid !== recordUid));
  saveTable(tc, 'record', loadTable(tc, 'record').filter((r) => r.recordUid !== recordUid));
}

/* ───────── 고객 요약(읽기 전용 파생값) ───────── */

export function customerSummaries(tc) {
  const services = byId(loadTable(tc, 'service'), 'serviceUid');
  const passes = loadTable(tc, 'pass');
  const records = loadTable(tc, 'record');
  return (customerUid) => {
    const active = passes.filter((p) => p.customerUid === customerUid && p.status === 'ACTIVE');
    const last = records.filter((r) => r.customerUid === customerUid).map((r) => r.visitDt).sort().pop() || '';
    return {
      passSummary: active
        .map((p) => `${services.get(p.serviceUid)?.serviceNm || '-'} ${passRemain(p)}/${num(p.totalCnt)}회${p.recurringYn === 'Y' ? '(정기)' : ''}`)
        .join(', '),
      remainCnt: active.reduce((s, p) => s + passRemain(p), 0),
      lastVisit: String(last).slice(0, 10),
    };
  };
}

/* ───────── 매출 ↔ 회원권 연동 ───────── */

/** 회원권 저장 후: 신규 회원권은 매출 자동 생성, 환불 처리된 회원권은 매출 환불 표시 */
export function syncPassSales(tc) {
  const passes = loadTable(tc, 'pass');
  const customers = byId(loadTable(tc, 'customer'), 'customerUid');
  const services = byId(loadTable(tc, 'service'), 'serviceUid');
  const sales = loadTable(tc, 'sale');
  let changed = false;
  for (const p of passes) {
    const sale = sales.find((s) => s.saleType === 'PASS' && s.refUid === p.passUid);
    if (!sale && p.status !== 'REFUND' && num(p.price) > 0) {
      sales.push({
        saleUid: createUid('SALE'),
        saleDate: p.payDate || today(),
        customerUid: p.customerUid,
        saleType: 'PASS',
        refUid: p.passUid,
        itemNm: `${services.get(p.serviceUid)?.serviceNm || ''} ${num(p.totalCnt)}회권 (${customers.get(p.customerUid)?.customerNm || ''})`,
        grossAmount: num(p.price),
        discountAmount: 0,
        netAmount: num(p.price),
        payMethod: 'CARD',
        refundYn: 'N',
        remark: '회원권 결제 자동 생성',
      });
      changed = true;
    }
    if (sale && p.status === 'REFUND' && sale.refundYn !== 'Y') {
      sale.refundYn = 'Y';
      changed = true;
    }
  }
  if (changed) saveTable(tc, 'sale', sales);
}

/* ───────── 월 손익 ───────── */

/** @param {string} ym 'YYYYMM' */
export function monthlyPnl(tc, ym) {
  const inMonth = (d) => String(d || '').replace(/-/g, '').slice(0, 6) === ym;
  const sales = loadTable(tc, 'sale').filter((s) => inMonth(s.saleDate) && s.refundYn !== 'Y');
  const outs = loadTable(tc, 'stockOut').filter((o) => inMonth(o.outDate));
  const expenses = loadTable(tc, 'expense').filter((e) => e.ym === ym);
  const revenue = sales.reduce((s, r) => s + num(r.netAmount), 0);
  const cogs = outs.filter((o) => o.reason === 'SERVICE' || o.reason === 'SALE').reduce((s, o) => s + num(o.amount), 0);
  const loss = outs.filter((o) => o.reason === 'DISCARD' || o.reason === 'ADJUST').reduce((s, o) => s + num(o.amount), 0);
  const byCategory = EXP_CATEGORY_OPTIONS.map((c) => ({
    category: c.value,
    label: c.label,
    amount: expenses.filter((e) => e.category === c.value).reduce((s, e) => s + num(e.amount), 0),
  }));
  const utilDetail = EXP_UTIL_OPTIONS.filter((o) => o.value).map((u) => ({
    label: u.label,
    amount: expenses.filter((e) => e.category === 'UTIL' && e.subCategory === u.value).reduce((s, e) => s + num(e.amount), 0),
  }));
  const expense = byCategory.reduce((s, c) => s + c.amount, 0);
  return {
    ym,
    revenue,
    cogs,
    grossProfit: revenue - cogs,
    loss,
    expense,
    byCategory,
    utilDetail,
    operatingProfit: revenue - cogs - loss - expense,
    purchase: loadTable(tc, 'stockIn').filter((l) => inMonth(l.inDate)).reduce((s, l) => s + num(l.amount), 0),
    saleCount: sales.length,
    recordCount: loadTable(tc, 'record').filter((r) => inMonth(r.visitDt)).length,
  };
}

/** 반복비용 템플릿 → 해당 월 비용 일괄 생성(이미 같은 템플릿으로 만든 건 건너뜀) */
export function generateMonthlyExpenses(tc, ym) {
  const tpls = loadTable(tc, 'expenseTpl').filter((t) => t.useYn !== 'N');
  const expenses = loadTable(tc, 'expense');
  let added = 0;
  for (const t of tpls) {
    if (expenses.some((e) => e.ym === ym && e.tplUid === t.tplUid)) continue;
    const day = String(Math.min(28, Math.max(1, num(t.payDay) || 1))).padStart(2, '0');
    expenses.push({
      expenseUid: createUid('EXP'),
      ym,
      category: t.category,
      subCategory: t.subCategory || '',
      amount: num(t.defaultAmount),
      payDate: `${ym.slice(0, 4)}-${ym.slice(4)}-${day}`,
      remark: t.remark || '',
      tplUid: t.tplUid,
    });
    added += 1;
  }
  if (added) saveTable(tc, 'expense', expenses);
  return added;
}
